import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, MessageCircle, Phone, HeartHandshake } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { outreachLocations, csrGallery } from '../data/csrData';
export function CSRInitiatives() {
  const {
    hash
  } = useLocation();
  useEffect(() => {
    if (hash) {
      document.querySelector(hash)?.scrollIntoView({
        behavior: 'smooth'
      });
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);
  return <div className="w-full bg-white" data-id="element-714" data-mp="3srg46qs73mkc">
      {/* Hero */}
      <section className="relative overflow-hidden bg-brand-navy" data-id="element-715" data-mp="1kdgi9bkfyev">
        <img src="https://cdn.magicpatterns.com/uploads/pibWsKQ5rJ6mMMinzR5oU9/csr_pics_3.jpg" alt="Maisha Medical Center outreach tents at a community health day" className="absolute inset-0 w-full h-full object-cover" data-id="element-716" data-mp="1j2cq9i4eqnr" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/95 via-brand-navy/75 to-brand-navy/30" data-id="element-717" data-mp="39zdszuyboyhh" />
        <div className="relative z-10 max-w-brand mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-28" data-id="element-718" data-mp="1ipiqzz97dds9">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: [0.23, 1, 0.32, 1]
        }} className="max-w-2xl text-white" data-id="element-719" data-mp="1epidqhxub8ms">
            <span className="font-semibold tracking-wider text-sm uppercase mb-3 block text-white" data-id="element-720" data-mp="zzkl9psvnwrs">
              CSR Initiatives
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold leading-tight mb-6 text-white" data-id="element-721" data-mp="uz6ba27pucr4">
              Free Community Health Services
            </h1>
            <p className="text-slate-200 text-lg leading-relaxed" data-id="element-722" data-mp="m0t763e0mb3n">
              Periodic free health screenings and wellness checks, open to
              everyone, to help build healthier communities across the region.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Reaching communities */}
      <section id="outreach" className="py-24 scroll-mt-32" data-id="element-723" data-mp="2zh4hsrgwd6u6">
        <div className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8" data-id="element-724" data-mp="349xmxgga2h6s">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-725" data-mp="34b8qpg9q3oxw">
            <div data-id="element-726" data-mp="2qfwlgexxyg2q">
              <span className="text-brand-red font-semibold tracking-wider text-sm uppercase mb-3 block" data-id="element-727" data-mp="2vh3tmf3zxh9x">
                Free Community Health Services
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-brand-navy mb-6 leading-tight" data-id="element-728" data-mp="3j4ka8khwfnl1">
                Reaching Communities Beyond Our Clinic
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6" data-id="element-729" data-mp="397z1pp6o7ucg">
                We care deeply about the wellbeing of our neighbors. From time
                to time we proudly offer free community health screenings and
                wellness checks to help you stay on top of your health. These
                periodic services are completely free of charge and open to the
                public as part of our mission to support our community.
              </p>
              <p className="text-slate-600 text-lg leading-relaxed mb-8" data-id="element-730" data-mp="1q8rhpnnc91k2">
                In the past, we have successfully brought these free services
                directly to our community at:
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 border-t border-slate-200" data-id="element-731" data-mp="1zb0qa7z1qkut">
                {outreachLocations.map(loc => <li key={loc.name} className="flex items-center gap-3 py-4 border-b border-slate-200" data-id="element-732" data-mp="17jl6smssde8t">
                    <MapPin className="w-5 h-5 text-brand-red shrink-0" data-id="element-733" data-mp="1lcsx8b54s3ox" />
                    <span className="font-heading font-semibold text-brand-navy" data-id="element-734" data-mp="1gj7wwcg2exmk">
                      {loc.name}
                    </span>
                  </li>)}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-735" data-mp="nv2all9d50km">
              <img src="https://cdn.magicpatterns.com/uploads/72BV1ZtPHHDZocyttRYEvX/csr_pics_4.jpg" alt="Maisha health worker checking a community member’s blood pressure" className="rounded-2xl shadow-soft object-cover w-full h-80 mt-10" data-id="element-736" data-mp="11ikrs9v4hztu" />
              <img src="https://cdn.magicpatterns.com/uploads/9uBZA2k7pd3NYg2ZR4kiAB/CSR_pics_5.jpg" alt="Maisha nurse assisting a young community member at a screening table" className="rounded-2xl shadow-soft object-cover w-full h-80" data-id="element-737" data-mp="1g778aewhkxmn" />
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20 bg-brand-lighter/40" data-id="element-738" data-mp="1z5jm4dycevok">
        <div className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8" data-id="element-739" data-mp="1z51opdzy2bf2">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-brand-navy mb-10" data-id="element-740" data-mp="3vz4ws2c715d9">
            Moments From Our Outreach
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-741" data-mp="4tmzcyauidzu">
            {csrGallery.map(img => <img key={img.src} src={img.src} alt={img.alt} className={img.wide ? 'col-span-2 rounded-2xl object-cover w-full h-64 md:h-80' : 'rounded-2xl object-cover object-top w-full h-64 md:h-80'} data-id="element-742" data-mp="2i3wf5wn0eevn" />)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24" data-id="element-743" data-mp="2wz2qmz4vj5wd">
        <div className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8" data-id="element-744" data-mp="2x040x1e2fod1">
          <div className="rounded-3xl bg-brand-navy px-8 py-14 md:px-16 md:py-16 text-center" data-id="element-745" data-mp="2rzg94di0j3hr">
            <div className="max-w-2xl mx-auto" data-id="element-746" data-mp="2n1xzekw68k8p">
              <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-6" data-id="element-747" data-mp="2n392sjlpfpza">
                <HeartHandshake className="w-7 h-7 text-brand-gold" data-id="element-748" data-mp="1blzla2m4z80m" />
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4" data-id="element-749" data-mp="1boccz7hp4wqv">
                Together, We Can Build Healthier Communities
              </h2>
              <p className="text-slate-200 text-lg mb-8" data-id="element-750" data-mp="1i1q0xi521q89">
                Want to know about our next free screening day, or partner with
                us on a community health initiative? Get in touch with our team.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-751" data-mp="ucnl8ftze7ge">
                <Button href="tel:+2676540244" variant="primary" size="lg" icon={<Phone className="w-5 h-5" data-id="element-753" data-mp="lc8uv225u261" />} data-id="element-752" data-mp="uaklh4310zyh">
                  Call Our Team
                </Button>
                <Button href="https://wa.me/26773241613" target="_blank" size="lg" icon={<MessageCircle className="w-5 h-5" data-id="element-755" data-mp="bfw3iau380eg" />} className="bg-[#25D366] hover:bg-[#20bd5a] focus:ring-[#25D366] text-white" data-id="element-754" data-mp="pem2jw0gwa5g">
                  Chat on WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}